/**
 * Language configuration for code block syntax highlighting
 */

export interface LanguageOption {
  value: string;
  label: string;
  group?: string;
}

/**
 * Supported programming languages for syntax highlighting
 * Organized by category for better UX
 */
export const SUPPORTED_LANGUAGES: LanguageOption[] = [
  // Popular/Common
  { value: 'plaintext', label: 'Plain Text', group: 'Common' },
  { value: 'javascript', label: 'JavaScript', group: 'Common' },
  { value: 'typescript', label: 'TypeScript', group: 'Common' },
  { value: 'python', label: 'Python', group: 'Common' },
  { value: 'json', label: 'JSON', group: 'Common' },

  // Web
  { value: 'html', label: 'HTML', group: 'Web' },
  { value: 'css', label: 'CSS', group: 'Web' },
  { value: 'scss', label: 'SCSS', group: 'Web' },
  { value: 'xml', label: 'XML', group: 'Web' },
  { value: 'jsx', label: 'JSX', group: 'Web' },
  { value: 'tsx', label: 'TSX', group: 'Web' },

  // Backend
  { value: 'java', label: 'Java', group: 'Backend' },
  { value: 'csharp', label: 'C#', group: 'Backend' },
  { value: 'php', label: 'PHP', group: 'Backend' },
  { value: 'ruby', label: 'Ruby', group: 'Backend' },
  { value: 'go', label: 'Go', group: 'Backend' },
  { value: 'rust', label: 'Rust', group: 'Backend' },
  { value: 'swift', label: 'Swift', group: 'Backend' },
  { value: 'kotlin', label: 'Kotlin', group: 'Backend' },

  // Systems
  { value: 'c', label: 'C', group: 'Systems' },
  { value: 'cpp', label: 'C++', group: 'Systems' },

  // Scripting
  { value: 'bash', label: 'Bash/Shell', group: 'Scripting' },
  { value: 'powershell', label: 'PowerShell', group: 'Scripting' },

  // Data
  { value: 'sql', label: 'SQL', group: 'Data' },
  { value: 'yaml', label: 'YAML', group: 'Data' },
  { value: 'graphql', label: 'GraphQL', group: 'Data' },

  // Other
  { value: 'markdown', label: 'Markdown', group: 'Other' },
  { value: 'dockerfile', label: 'Dockerfile', group: 'Other' },
];

/**
 * Get the display label for a language value
 */
export const getLanguageLabel = (value: string): string => {
  const language = SUPPORTED_LANGUAGES.find(lang => lang.value === value);
  return language ? language.label : value;
};

/**
 * Check if a language is supported
 */
export const isValidLanguage = (value: string): boolean => {
  return SUPPORTED_LANGUAGES.some(lang => lang.value === value);
};

/**
 * Get languages grouped by category
 */
export const getGroupedLanguages = (): Record<string, LanguageOption[]> => {
  return SUPPORTED_LANGUAGES.reduce((acc, lang) => {
    const group = lang.group || 'Other';
    if (!acc[group]) {
      acc[group] = [];
    }
    acc[group].push(lang);
    return acc;
  }, {} as Record<string, LanguageOption[]>);
};

/**
 * Search/filter languages by query string
 */
export const filterLanguages = (query: string): LanguageOption[] => {
  const lowerQuery = query.toLowerCase().trim();
  if (!lowerQuery) {
    return SUPPORTED_LANGUAGES;
  }

  return SUPPORTED_LANGUAGES.filter(lang =>
    lang.label.toLowerCase().includes(lowerQuery) ||
    lang.value.toLowerCase().includes(lowerQuery)
  );
};
